import { useState } from "react";
import axios from "axios";
import { API } from "../../config";
import { CheckCircle, ChevronDown, Copy, FileText, Loader2, PenLine, Sparkles } from "lucide-react";

export const ContentWriterTool = ({ t, language, userEmail }) => {
  const [contentType, setContentType] = useState('blog_post');
  const [topic, setTopic] = useState('');
  const [keywords, setKeywords] = useState('');
  const [tone, setTone] = useState('professional');
  const [wordCount, setWordCount] = useState(500);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const contentTypes = [
    { value: 'blog_post', label: 'Blog Post' },
    { value: 'landing_page', label: 'Landing Page' },
    { value: 'ad_copy', label: 'Ad Copy' },
    { value: 'meta_description', label: 'Meta Description' },
    { value: 'social_post', label: 'Social Post' },
    { value: 'product_description', label: 'Product Description' },
  ];

  const tones = [
    { value: 'professional', label: 'Professional' },
    { value: 'casual', label: 'Casual' },
    { value: 'persuasive', label: 'Persuasive' },
    { value: 'informative', label: 'Informative' },
  ];

  const handleGenerate = async () => {
    if (!topic.trim() || isLoading) return;
    setIsLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/content-writer`, {
        content_type: contentType,
        topic: topic.trim(),
        keywords: keywords.split(',').map(k => k.trim()).filter(Boolean),
        tone,
        language,
        word_count: wordCount,
        email: userEmail || ''
      });
      setResult(res.data);
    } catch (e) {
      console.error('Content writer error:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = () => {
    if (result?.content) {
      navigator.clipboard.writeText(result.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const templates = [
    {
      category: t.tpl_ecommerce,
      icon: '🛒',
      color: 'border-[var(--color-border)] bg-[var(--color-surface-alt)]/50 hover:border-zinc-500',
      items: [
        { label: 'Product Description', contentType: 'product_description', tone: 'persuasive', words: 200, topic: 'Product features and benefits for online store', keywords: 'buy, best, review, price, quality' },
        { label: 'Category Page', contentType: 'landing_page', tone: 'informative', words: 400, topic: 'Product category overview page with SEO optimization', keywords: 'shop, collection, best sellers, deals' },
        { label: 'Post-Purchase Email', contentType: 'ad_copy', tone: 'casual', words: 150, topic: 'Thank you email after purchase with upsell', keywords: 'thank you, order, recommend, discount' },
      ]
    },
    {
      category: t.tpl_saas,
      icon: '🚀',
      color: 'border-[var(--color-border)] bg-[var(--color-surface)] hover:border-blue-400',
      items: [
        { label: 'Landing Page', contentType: 'landing_page', tone: 'persuasive', words: 600, topic: 'SaaS product landing page with features and pricing CTA', keywords: 'free trial, features, pricing, sign up' },
        { label: 'Onboarding Email', contentType: 'ad_copy', tone: 'casual', words: 200, topic: 'Welcome email for new SaaS user onboarding', keywords: 'welcome, getting started, first steps, help' },
        { label: 'Feature Announcement', contentType: 'blog_post', tone: 'professional', words: 500, topic: 'New feature announcement blog post', keywords: 'new feature, update, improvement, launch' },
      ]
    },
    {
      category: t.tpl_local,
      icon: '📍',
      color: 'border-[var(--color-border)] bg-[var(--color-surface-alt)]/50 hover:border-zinc-500',
      items: [
        { label: 'Google Profile', contentType: 'meta_description', tone: 'professional', words: 50, topic: 'Google Business Profile description for local business', keywords: 'near me, local, best, open now' },
        { label: 'Local Blog Post', contentType: 'blog_post', tone: 'informative', words: 800, topic: 'Local area guide and business expertise article', keywords: 'city, neighborhood, local tips, guide' },
        { label: 'Review Response', contentType: 'social_post', tone: 'professional', words: 80, topic: 'Professional response to a positive customer review', keywords: 'thank you, appreciate, visit again' },
      ]
    },
    {
      category: t.tpl_blog,
      icon: '📝',
      color: 'border-[var(--color-border)] bg-[var(--color-surface)] hover:border-[#a855f7]',
      items: [
        { label: 'SEO Article', contentType: 'blog_post', tone: 'informative', words: 1200, topic: 'In-depth SEO-optimized article with expert insights', keywords: 'guide, tips, how to, best practices' },
        { label: 'How-To Guide', contentType: 'blog_post', tone: 'informative', words: 1000, topic: 'Step-by-step tutorial guide for beginners', keywords: 'step by step, tutorial, beginners, learn' },
        { label: 'Listicle', contentType: 'blog_post', tone: 'casual', words: 800, topic: 'Top 10 list article with actionable tips', keywords: 'top, best, list, must-have, essential' },
      ]
    },
    {
      category: t.tpl_social,
      icon: '💬',
      color: 'border-[var(--color-border)] bg-[var(--color-surface-alt)]/50 hover:border-[#67e8f9]',
      items: [
        { label: 'Instagram Post', contentType: 'social_post', tone: 'casual', words: 50, topic: 'Engaging Instagram caption with hashtags', keywords: 'instagood, trending, lifestyle' },
        { label: 'LinkedIn Post', contentType: 'social_post', tone: 'professional', words: 100, topic: 'Professional LinkedIn thought leadership post', keywords: 'business, growth, leadership, insights' },
        { label: 'Facebook Ad', contentType: 'ad_copy', tone: 'persuasive', words: 80, topic: 'Facebook ad copy with compelling CTA', keywords: 'limited time, exclusive, sign up, free' },
      ]
    },
  ];

  const applyTemplate = (item) => {
    setContentType(item.contentType);
    setTone(item.tone);
    setWordCount(item.words);
    setTopic(item.topic);
    setKeywords(item.keywords);
    setResult(null);
  };

  const [showTemplates, setShowTemplates] = useState(true);

  return (
    <div data-testid="content-writer-tool">
      {/* Templates Section */}
      <div className="mb-8" data-testid="content-templates">
        <button
          data-testid="toggle-templates"
          onClick={() => setShowTemplates(!showTemplates)}
          className="flex items-center gap-2 text-sm font-semibold text-[var(--color-text-muted)] hover:text-[#a855f7] transition-colors mb-4"
        >
          <FileText className="w-4 h-4" />
          {t.templates_title}
          <ChevronDown className={`w-4 h-4 transition-transform ${showTemplates ? 'rotate-180' : ''}`} />
        </button>
        {showTemplates && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 animate-fade-in">
            {templates.map((cat, ci) => (
              <div key={ci} className={`border rounded-lg p-3 transition-all ${cat.color}`}>
                <div className="flex items-center gap-2 mb-2.5">
                  <span className="text-lg">{cat.icon}</span>
                  <span className="text-xs font-bold text-[var(--color-text)] uppercase tracking-wide">{cat.category}</span>
                </div>
                <div className="space-y-1.5">
                  {cat.items.map((item, ii) => (
                    <button
                      key={ii}
                      data-testid={`template-${ci}-${ii}`}
                      onClick={() => applyTemplate(item)}
                      className="w-full text-left text-xs px-2.5 py-1.5 bg-[var(--color-surface)]/70 hover:bg-[var(--color-surface)] rounded border border-transparent hover:border-[var(--color-border)] transition-all font-medium text-[var(--color-text)] hover:text-[#a855f7]"
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Input Form */}
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-[var(--color-text)] mb-1">{t.content_type}</label>
          <select
            data-testid="content-type-select"
            value={contentType}
            onChange={(e) => setContentType(e.target.value)}
            className="w-full px-4 py-3 border-2 border-[var(--color-border)] focus:border-[#a855f7] focus:outline-none bg-[var(--color-surface)]"
          >
            {contentTypes.map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--color-text)] mb-1">{t.content_tone}</label>
          <select
            data-testid="content-tone-select"
            value={tone}
            onChange={(e) => setTone(e.target.value)}
            className="w-full px-4 py-3 border-2 border-[var(--color-border)] focus:border-[#a855f7] focus:outline-none bg-[var(--color-surface)]"
          >
            {tones.map(tn => <option key={tn.value} value={tn.value}>{tn.label}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-[var(--color-text)] mb-1">{t.content_topic}</label>
          <input
            type="text"
            data-testid="content-topic-input"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleGenerate()}
            placeholder="e.g. How to improve SEO for small businesses"
            className="w-full px-4 py-3 border-2 border-[var(--color-border)] focus:border-[#a855f7] focus:outline-none transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--color-text)] mb-1">{t.content_keywords}</label>
          <input
            type="text"
            data-testid="content-keywords-input"
            value={keywords}
            onChange={(e) => setKeywords(e.target.value)}
            placeholder="seo, ranking, optimization"
            className="w-full px-4 py-3 border-2 border-[var(--color-border)] focus:border-[#a855f7] focus:outline-none transition-colors"
          />
        </div>
        <div className="flex items-end gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-[var(--color-text)] mb-1">Words: {wordCount}</label>
            <input
              type="range"
              data-testid="content-wordcount-slider"
              min="50"
              max="2000"
              step="50"
              value={wordCount}
              onChange={(e) => setWordCount(Number(e.target.value))}
              className="w-full accent-blue-600"
            />
          </div>
          <button
            data-testid="content-generate-btn"
            onClick={handleGenerate}
            disabled={isLoading || !topic.trim()}
            className="px-6 py-3 bg-[#a855f7] text-white font-semibold hover:bg-purple-600 transition-colors disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
          >
            {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <PenLine className="w-5 h-5" />}
            {t.content_generate}
          </button>
        </div>
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-6 animate-fade-in" data-testid="content-results">
          {/* Header with SEO Score + Copy */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-[var(--color-text-muted)]">{t.seo_score}:</span>
                <div className={`px-3 py-1 font-mono font-bold text-sm rounded ${
                  result.seo_score >= 80 ? 'bg-green-100 text-green-700' :
                  result.seo_score >= 60 ? 'bg-yellow-100 text-yellow-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {result.seo_score}/100
                </div>
              </div>
              {result.keywords_used.length > 0 && (
                <div className="flex gap-1 flex-wrap">
                  {result.keywords_used.map((kw, i) => (
                    <span key={i} className="px-2 py-0.5 bg-[var(--color-surface)] text-[#a855f7] text-xs rounded">{kw}</span>
                  ))}
                </div>
              )}
            </div>
            <button
              data-testid="copy-content-btn"
              onClick={handleCopy}
              className="flex items-center gap-2 px-4 py-2 border border-[var(--color-border)] hover:bg-[var(--color-bg)] transition-colors text-sm font-medium"
            >
              {copied ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : t.copy_content}
            </button>
          </div>

          {/* Content Output */}
          <div className="border border-[var(--color-border)] bg-[var(--color-surface)]">
            <div className="p-4 border-b border-[var(--color-border)] bg-[var(--color-bg)]">
              <h3 className="font-bold font-['Chivo']">{result.title}</h3>
              <p className="text-xs text-[var(--color-text-muted)] mt-1">{result.meta_description}</p>
            </div>
            <div className="p-6">
              <div className="prose prose-sm max-w-none whitespace-pre-wrap text-[var(--color-text)] leading-relaxed" data-testid="content-output">
                {result.content}
              </div>
            </div>
          </div>

          {/* Suggestions */}
          {result.suggestions && result.suggestions.length > 0 && (
            <div className="border border-[var(--color-border)] bg-[var(--color-surface)]" data-testid="content-suggestions">
              <div className="p-4 border-b border-[var(--color-border)] bg-[var(--color-bg)]">
                <h4 className="font-bold text-sm flex items-center gap-2 font-['Chivo']">
                  <Sparkles className="w-4 h-4 text-[#a855f7]" />
                  {t.suggestions}
                </h4>
              </div>
              <div className="p-4 space-y-2">
                {result.suggestions.map((s, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>{s}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
